import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { mockUsers } from '../../../lib/mock-data';
import { SkeletonTable } from '../../../components/common/SkeletonLoader';

/**
 * Admin Users - User management and moderation
 */
export const Route = createFileRoute('/_authenticated/admin/users')({
  component: AdminUsers,
});

function AdminUsers() {
  const [users, setUsers] = useState<typeof mockUsers>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { theme } = useTheme();

  useEffect(() => {
    // Simulate API call
    const timer = setTimeout(() => {
      setUsers(mockUsers);
      setLoading(false);
    }, 900);

    return () => clearTimeout(timer);
  }, []);

  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      dashedBorder: 'rgba(220, 38, 38, 0.3)',
      tableBg: 'rgba(15, 15, 20, 0.4)',
      tableHeaderBg: 'rgba(26, 26, 46, 0.6)',
      inputBg: 'rgba(26, 26, 46, 0.6)',
      inputBorder: 'rgba(220, 38, 38, 0.3)',
      buttonBg: '#dc2626',
      headerColor: '#ef4444',
      avatarBg: 'rgba(220, 38, 38, 0.2)',
      avatarText: '#d1d5db'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      dashedBorder: '#e5e7eb',
      tableBg: '#ffffff',
      tableHeaderBg: '#f9fafb',
      inputBg: '#ffffff',
      inputBorder: '#e5e7eb',
      buttonBg: '#dc2626',
      headerColor: '#dc2626',
      avatarBg: '#fecaca',
      avatarText: '#991b1b'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <h1 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: c.headerColor,
              marginBottom: '0.5rem'
            }}>
              User Management
            </h1>
            <p style={{ color: c.textMuted, fontSize: '1rem' }}>
              Manage user accounts, roles, and permissions
            </p>
          </div>
          <button style={{
            backgroundColor: c.buttonBg,
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500'
          }}>
            + Add User
          </button>
        </div>

        {/* Search and Filters */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              border: `1px solid ${c.inputBorder}`,
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              backgroundColor: c.inputBg,
              color: c.text
            }}
          />
          <select style={{
            padding: '0.75rem 1rem',
            border: `1px solid ${c.inputBorder}`,
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: c.inputBg,
            color: c.text
          }}>
            <option>All Roles</option>
            <option>Admin</option>
            <option>Creator</option>
            <option>User</option>
          </select>
          <select style={{
            padding: '0.75rem 1rem',
            border: `1px solid ${c.inputBorder}`,
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            backgroundColor: c.inputBg,
            color: c.text
          }}>
            <option>All Status</option>
            <option>Active</option>
            <option>Suspended</option>
          </select>
        </div>

        {/* Users Table */}
        {loading ? (
          <SkeletonTable rows={8} columns={6} theme={theme} />
        ) : users.length === 0 ? (
          <div style={{
            background: c.cardBg,
            border: `2px dashed ${c.dashedBorder}`,
            borderRadius: '0.5rem',
            padding: '3rem',
            textAlign: 'center',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <p style={{
              fontSize: '1.125rem',
              color: c.textMuted,
              marginBottom: '1rem'
            }}>
              No users found
            </p>
            <p style={{
              fontSize: '0.875rem',
              color: c.textMuted,
              opacity: 0.7
            }}>
              Users will appear here as they register
            </p>
          </div>
        ) : (
          <div style={{
            background: c.tableBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '0.5rem',
            overflow: 'hidden',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: c.tableHeaderBg }}>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>User</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Email</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Role</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Joined</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: c.headerColor, textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderTop: `1px solid ${c.cardBorder}` }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.text }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                          width: '2rem',
                          height: '2rem',
                          borderRadius: '50%',
                          backgroundColor: c.avatarBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: c.avatarText
                        }}>
                          {user.name.charAt(0)}
                        </div>
                        <span>{user.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>{user.email}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: user.role === 'admin' ? '#dc2626' :
                                       user.role === 'creator' ? '#2563eb' : '#059669',
                        color: 'white'
                      }}>
                        {user.role}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: user.status === 'active' ? '#d1fae5' : '#fee2e2',
                        color: user.status === 'active' ? '#065f46' : '#991b1b'
                      }}>
                        {user.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: c.textMuted }}>
                      {new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: theme === 'dark' ? 'rgba(220, 38, 38, 0.2)' : '#fef2f2',
                          border: `1px solid ${c.cardBorder}`,
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          color: c.headerColor
                        }}>
                          Edit
                        </button>
                        <button style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : 'white',
                          border: `1px solid ${c.cardBorder}`,
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          color: '#dc2626'
                        }}>
                          Suspend
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsers;
